/**
 * The arithmetic of journal citation metrics.
 *
 * - Journal Impact Factor (Clarivate, JCR year Y): citations received in Y to items the
 *   journal published in Y−1 and Y−2, divided by the citable items (articles and
 *   reviews) it published in those two years. The numerator counts citations to every
 *   item; only the denominator is restricted to citable ones.
 * - Five-year JIF: the same over Y−1 … Y−5.
 * - CiteScore (Elsevier Scopus, year Y): citations received in Y−3 … Y to articles,
 *   reviews, conference papers, book chapters and data papers published in Y−3 … Y,
 *   divided by the number of those documents.
 *
 * Official values come from Clarivate's Journal Citation Reports and from Scopus; this
 * only reproduces the division from counts the user supplies.
 */

export interface MetricResult {
  numerator: number;
  denominator: number;
  value: number;
}

/** A total, or null while any of the parts is missing. */
export function completeSum(values: readonly (number | null)[]): number | null {
  let sum = 0;
  for (const value of values) {
    if (value === null || !Number.isFinite(value)) {
      return null;
    }
    sum += value;
  }
  return sum;
}

/** Citations ÷ items over matching years; null if any count is missing or items are 0. */
export function citationRatio(
  citations: readonly (number | null)[],
  items: readonly (number | null)[]
): MetricResult | null {
  const numerator = completeSum(citations);
  const denominator = completeSum(items);
  if (numerator === null || denominator === null || denominator <= 0) {
    return null;
  }
  return { numerator, denominator, value: numerator / denominator };
}

/** The years a metric for year `year` looks back over, newest first. */
export function windowYears(year: number, span: number, includeYear: boolean) {
  const start = includeYear ? 0 : 1;
  return Array.from({ length: span }, (_, index) => year - start - index);
}

/** One decimal place, the precision JCR has displayed since its 2023 release. */
export function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}
